/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useRef, useState } from 'react';
import { useParams, NavLink, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Iframe from 'react-iframe';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDSnackbar from 'components/MDSnackbar';
import MDInput from 'components/MDInput';
import TableHeader from 'components/Table/TableHeader';
import MDTypography from 'components/MDTypography';
import MDSelect from 'components/MDSelect';

// Material Dashboard 2 PRO React examples
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import Footer from 'examples/Footer';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';
import TagList from './TagList';
import TagFromCampaign from './TagFromCampaign'; // งานข้อ 5 — สร้างป้ายจากแคมเปญ
import TagExportPng from './TagExportPng'; // งานข้อ 5 — ส่งออก PNG
import TagRankPicker, {
  RANK_EMPTY,
  SheetPreview,
  createRankTags,
  createImportTags,
  fileMerge,
  perPageOf,
  rankErrorText,
} from './TagRankPicker'; // 29 ต.ค. 2569 — หมวด/A-B-C · ภาพย่อแผ่นกระดาษ
import { tagArtErrorText } from './TagArtPanel'; // 29 ต.ค. 2569 — ภาพบนป้ายโปร (ตัวหน้าอยู่ใน TagList → TagArtPanel ตั้งแต่ 2 พ.ย. 2569)

const initAttr = {
  id: '',
  code: '',
  branch_id: '',
  template_id: '0', // งานข้อ 5 — แม่แบบพิมพ์ (tag_templates) 0 = ค่าตั้งต้น A4
  print: '',
  sticker: '',
  print_no: '',
  remark: '',
  sticker_no: '',
  enable: 'Y',
  created_at: '',
  updated_at: '',
};

const initFind = {
  search: '',
  order_by: 'tb.code',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  status: 'ALL', // ALL · WAITING (มีรูป AI รออนุมัติ) · NEVER (ยังไม่เคยพิมพ์) · PRINTED
  period: 'ALL', // ALL หรือจำนวนวันย้อนหลัง ('7' · '30')
  kind: 'ALL', // ALL · PROMO · CLASSIC (แบบป้าย)
  branch: 'ALL',
  date_start: Func.dateTimeFirstMonth(),
  date_stop: Func.dateTimeEndMonth(),
};

const initNavigate = {
  first: '',
  prev: '',
  next: '',
  last: '',
};

function Tag() {
  const mod = 'tag';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [attr, setAttr] = useState(initAttr);
  const [find, setFind] = useState(initFind);
  const [navigate, setNavigate] = useState(initNavigate);
  const [notify, setNotify] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [modalAdd, setModalAdd] = useState(false);
  const [modalDel, setModalDel] = useState(false);
  const [modalPrint, setModalPrint] = useState(false);
  const [cboBranch, setCboBranch] = useState([]);
  const [cboTemplate, setCboTemplate] = useState([]); // งานข้อ 5
  const [modalCampaign, setModalCampaign] = useState(false); // งานข้อ 5
  const [jump, setJump] = useState(null); // แถบ "ก่อนพิมพ์" → หน้าชุดป้าย: {kind: 'review' | 'unprinted', n}
  const [saved, setSaved] = useState(null); // ค่าที่บันทึกในฐานแล้ว — ใช้ดูว่ามีแม่แบบ/สาขาที่ยังไม่บันทึกไหม (18 ต.ค. 2569)
  const [rank, setRank] = useState(RANK_EMPTY); // เลือกสินค้าจากยอดขาย (TagRankPicker)
  const [rankStatus, setRankStatus] = useState({ active: false, reason: '' });
  const [artSum, setArtSum] = useState(null); // ยอดจาก TagArtPanel — แถบก่อนพิมพ์ + ถามก่อนพิมพ์
  /* ติ๊กหลายชุดในตาราง → พิมพ์รวม / STICKER รวม / เปลี่ยนเป็นป้ายโปร / ทำภาพที่ยังไม่มี (ตรวจการใช้งาน 2.4 · 1 พ.ย. 2569) */
  const [pickedTags, setPickedTags] = useState({});
  const [priceTick, setPriceTick] = useState(0);
  /* ราคาบนป้ายของชุดใหม่ (ป้ายโปร) — เลือกตอนสร้าง จำค่าล่าสุดในเครื่อง (ตรวจการใช้งาน 1.5 · 1 พ.ย. 2569) */
  const [newLevels, setNewLevels] = useState(() => {
    try {
      return ['1', '2', '3'].includes(localStorage.getItem('tag.price_levels'))
        ? localStorage.getItem('tag.price_levels')
        : '2';
    } catch (e) {
      return '2';
    }
  });
  const [artMeta, setArtMeta] = useState(null); // สั่งทำภาพบนป้ายได้ไหม + เครดิตต่อสินค้า (หน้าต่าง "+ เพิ่ม")
  const [artAuto, setArtAuto] = useState(true); // สร้างป้ายโปรแล้วสั่งทำภาพให้สินค้าที่ยังไม่มีต่อเลย
  const [addStep, setAddStep] = useState(1); // หน้าต่างสร้างชุด 3 ขั้น (2 พ.ย. 2569): 1 ตั้งค่าชุด · 2 เลือกสินค้า · 3 ตรวจก่อนสร้าง
  const [addSource, setAddSource] = useState('rank'); // rank = ตามยอดขาย · file = จากไฟล์ · empty = ชุดเปล่า
  const notifyTimer = useRef(null);
  const alive = useRef(true); // กัน setState หลังออกจากหน้า (ตั้ง true ใน effect ด้วย — Fast Refresh)
  const routerNavigate = useNavigate();

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'tb.id', label: t(`attr.id`), align: 'center', width: 30 },
    { id: 'tb.code', label: t(`attr.code`), align: 'center' },
    { id: 'tb.branch_id', label: 'สาขา', align: 'left' },
    { id: 'tb.remark', label: 'หมายเหตุ', align: 'left' },
    { id: 'count_tag', label: 'ป้าย', align: 'left', sort: false },
    { id: 'status', label: 'สถานะ', align: 'left', sort: false },
    { id: 'tb.user_id', label: 'ผู้สร้าง', align: 'left' },
    { id: 'tb.created_at', label: t(`attr.created_at`), align: 'center' },
    { id: 'delete', label: '', align: 'center', sort: false, width: 40 },
  ];

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    /* error ค้าง 8 วิ ให้อ่านทัน · success 3 วิ เหมือนเดิม — ล้างตัวจับเวลาเก่าไม่ให้ปิดข้อความใหม่ก่อนเวลา (18 ต.ค. 2569) */
    clearTimeout(notifyTimer.current);
    notifyTimer.current = setTimeout(
      function () {
        if (alive.current) setNotify(null);
      },
      color === 'error' ? 8000 : 3000
    );
  };

  const view = (data) => {
    setFind(data);
    setLoading(true);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      status: data.status,
      period: data.period,
      kind: data.kind,
      date_start: data.date_start,
      date_stop: data.date_stop,
      branch: data.branch,
    })
      .then((result) => {
        if (result.status === 'ok') {
          setRecord(result.data);

          setPageTotal(result.total_pages);
          setPageNo(result.page);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        showNotify(t(`alert.loaderror`), 'error');
      });
  };

  const open = () => {
    setLoading(true);
    setConfirm(false);
    setAttr(initAttr);
    setSaved(null);
    setJump(null);
    setArtSum(null); // ยอดรออนุมัติของป้ายก่อนหน้า ไม่ค้างบนแถบ "ก่อนพิมพ์" ของป้ายใหม่

    FetchData('GET', `/${mod}/${id}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setAttr(result.data);
        setSaved(result.data);
        setNavigate(result.navigate);

        setLoading(false);
      }
    });
  };

  const addConfirm = () => {
    if (!Func.verifyLength(attr.branch_id, 1)) return;
    if (rankStatus.active && rankStatus.reason) {
      showNotify(rankStatus.reason, 'error');
      return;
    }

    setConfirm(true);
  };

  /* แม่แบบที่เลือกในหน้าต่างเพิ่มเป็นป้ายโปร + สั่งทำภาพได้ = เสนอให้ระบบทำภาพบนป้ายต่อเลย (29 ต.ค. 2569 · "หลังบ้านจัดการเอง") */
  const addTpl = cboTemplate.find((x) => String(x.id) === String(attr.template_id ?? '0'));
  const artOn = Boolean(
    addTpl &&
      addTpl.layout === 'PROMO' &&
      artMeta &&
      artMeta.ready === 'Y' &&
      artMeta.can_generate === 'Y' &&
      artMeta.ai_ready === 'Y' &&
      artMeta.ai_off !== 'Y' &&
      artMeta.mascot_fixed !== 'Y' // ช้างของแบรนด์คงที่ (พี่นก) — ไม่เสนอทำภาพช้างตอนสร้าง
  );
  /** สั่งทำภาพให้ทุกชุดที่เพิ่งสร้าง — คืนข้อความต่อท้าย toast · พลาดไม่ล้มการสร้างป้าย (กดทำต่อในแท็บภาพบนป้ายได้) */
  const orderArt = (tags) =>
    tags
      .reduce(
        (chain, tg) =>
          chain.then((sum) =>
            FetchData('OPTIONS', '/tag_art/generate', app.token, { tag_id: tg.id, _t: Date.now() })
              .catch(() => null)
              .then((r) =>
                r && r.status === 'ok'
                  ? {
                      ...sum,
                      count: sum.count + Number(r.count || 0),
                      credits: sum.credits + Number(r.credits || 0),
                    }
                  : { ...sum, fail: sum.fail || tagArtErrorText(r && r.msg) }
              )
          ),
        Promise.resolve({ count: 0, credits: 0, fail: '' })
      )
      .then((sum) => {
        const ok = sum.count
          ? ` · สั่งทำภาพ ${sum.count} สินค้า ≈ ${sum.credits} เครดิต (ดูที่หน้าชุดป้าย)`
          : '';
        /* บางชุดสำเร็จ บางชุดติดโควตา — บอกทั้งสองอย่าง ชุดที่สั่งไปแล้วถูกคิดเครดิต (รีวิว 29 ต.ค.) */
        if (sum.fail) return `${ok} · สั่งทำภาพ${sum.count ? 'บางชุด' : ''}ไม่สำเร็จ: ${sum.fail}`;
        return ok;
      });

  /* ตั้งราคาบนป้ายให้ชุดที่เพิ่งสร้าง (ป้ายโปรเท่านั้น · ค่าตั้งต้นของระบบ = 2) — ไม่ต้องเข้าไปตั้งทีละชุด */
  const applyLevels = (ids) => {
    if (!(addTpl && addTpl.layout === 'PROMO') || newLevels === '2' || !ids.length)
      return Promise.resolve();
    return ids.reduce(
      (p, tagId) =>
        p.then(() =>
          FetchData('OPTIONS', '/tag_art/levels', app.token, {
            tag_id: tagId,
            levels: Number(newLevels),
            _t: Date.now(),
          }).catch(() => null)
        ),
      Promise.resolve()
    );
  };

  /* เลือกหมวดใน TagRankPicker = สร้างป้ายพร้อมสินค้าเรียงขายดี · เกิน 200 รายการได้หลายชุด (29 ต.ค. 2569) */
  const addRank = () => {
    setLoading(true);
    setModalAdd(false);
    const base = { branch_id: attr.branch_id, template_id: attr.template_id, remark: attr.remark };
    /* อัปไฟล์รหัสสินค้า (31 ต.ค. 2569) = ป้ายเรียงตามไฟล์ · ไม่งั้นเรียงตามยอดขาย */
    const req = rank.file
      ? createImportTags(app.token, {
          ...base,
          product_ids: fileMerge(rank.file).ids,
          name: rank.file.name,
        })
      : createRankTags(app.token, {
          ...base,
          cat_id: rank.cat_id,
          subcat_id: rank.subcat_id || 0,
          months: rank.months,
          abc: rank.abc,
          limit: rankStatus.limit || 0,
        });
    req
      .then(async (result) => {
        if (result.status === 'ok') {
          const first = result.tags[0];
          await applyLevels(result.tags.map((x) => x.id));
          setRank(RANK_EMPTY);
          /* ระหว่างตรวจไฟล์กับกดยืนยัน สินค้าถูกปิดขาย = ไม่ได้ป้าย บอกให้รู้ */
          const gone =
            rank.file && Number(result.skipped) > 0
              ? ` · ข้าม ${result.skipped} สินค้าที่เพิ่งปิดขาย`
              : '';
          const art = (artAuto && artOn ? await orderArt(result.tags) : '') + gone;
          setLoading(false);
          if (result.tags.length === 1) {
            showNotify(`สร้างป้าย ${first.code} ${result.total} รายการ${art}`, 'success');
            routerNavigate(`/${mod}/${first.id}`);
          } else {
            /* ล้างคำค้นและกรองสาขาที่สร้าง ไม่ให้ตัวกรองเดิมซ่อนป้ายใหม่ · ชุด 1 ได้รหัสสูงสุดจึงอยู่บนสุด */
            showNotify(
              `สร้างป้าย ${result.tags.length} ชุด รวม ${result.total} รายการ · ชุด 1 = ${first.code}${art}`,
              'success'
            );
            view({
              ...find,
              search: '',
              kind: 'ALL',
              status: 'ALL',
              period: 'ALL',
              branch: String(attr.branch_id),
              page: 1,
            });
          }
        } else {
          setLoading(false);
          setModalAdd(true);
          showNotify(rankErrorText(result), 'error');
        }
      })
      .catch(() => {
        setLoading(false);
        setModalAdd(true);
        showNotify('เชื่อมต่อไม่สำเร็จ ลองอีกครั้ง', 'error');
      });
  };

  const add = () => {
    if (rankStatus.active) {
      /* ตรวจซ้ำตอนกดยืนยัน — เปลี่ยนสาขาหลังกดบันทึก ผลนับอาจยังไม่มา/เกินเพดาน (รีวิว 29 ต.ค.) */
      if (rankStatus.reason) {
        setConfirm(false);
        showNotify(rankStatus.reason, 'error');
        return;
      }
      addRank();
      return;
    }
    setLoading(true);
    setModalAdd(false);

    FetchData('PUT', `/${mod}`, app.token, {
      branch_id: attr.branch_id,
      remark: attr.remark,
      template_id: attr.template_id,
    }).then(async (result) => {
      if (result.status === 'ok') await applyLevels([result.id]);
      setLoading(false);

      if (result.status === 'ok') {
        window.location.replace(`${mod}/${result.id}`);
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        setModalAdd(true);
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const copy = () => {
    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('OPTIONS', `/${mod}/copy`, app.token, {
          id: id,
        }).then((result) => {
          if (result.status === 'ok') {
            window.location.replace(`/${mod}/${result.id}`);
            showNotify(t(`alert.addcomplete`), 'success');
          } else {
            setLoading(false);
            showNotify(t(`alert.saveerror`), 'error');
          }
        });
      }
    });
  };

  const edit = () => {
    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('PATCH', `/${mod}/${attr.id}`, app.token, {
          branch_id: attr.branch_id,
          remark: attr.remark,
          template_id: attr.template_id,
        }).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            setSaved(attr);
            showNotify(t(`alert.editcomplete`), 'success');
          } else {
            showNotify(t(`alert.saveerror`), 'error');
          }
        });
      }
    });
  };

  const del = (item) => {
    Swal.fire({
      title: t(`alert.confirm_delete_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.deleteconfirm`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('DELETE', `/${mod}/${item.id}`, app.token, {}).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            setPickedTags((cur) => {
              const next = { ...cur };
              delete next[item.id];
              return next;
            });
            view(find);
            showNotify(t(`alert.deletecomplete`), 'success');
          } else {
            showNotify(t(`alert.deleteerror`), 'error');
          }
        });
      }
    });
  };

  /* ป้ายจากแคมเปญนอกช่วงวันที่ + ผู้ใช้มีสิทธิ์ campaign_expired → ถามยืนยันก่อนพิมพ์/สติกเกอร์/PNG ทุกครั้ง
     คืน 'Y' = ยืนยันแล้ว · 'N' = ไม่ต้องถาม (server ตัดสินเอง) · null = ยกเลิก
     แคมเปญปิดใช้งาน (DRAFT) ไม่มีใครพิมพ์ได้ จึงไม่ถาม (16 ต.ค. 2569) */
  const confirmExpired = () => {
    const cp = attr.campaign;
    const canOverride = Boolean(app.perm && app.perm.campaign_expired);
    if (!cp || ['ACTIVE', 'DRAFT'].includes(cp.status) || !canOverride) {
      return Promise.resolve('N');
    }
    return Swal.fire({
      title: 'แคมเปญนี้อยู่นอกช่วงวันที่ — ยืนยันพิมพ์/ส่งออก?',
      text: `${cp.code} · ${cp.status_label}`,
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'ยืนยัน',
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => (swal.isConfirmed ? 'Y' : null));
  };

  /* เหตุผลที่พิมพ์/สติกเกอร์/PNG ไม่ได้ — แสดงข้างปุ่มและปิดปุ่ม แทนการกดแล้วเจอหน้าแจ้งเตือนเต็มจอ (18 ต.ค. 2569)
     ปิดใช้งาน = ไม่มีใครพิมพ์ได้ · หมดอายุ/ยังไม่เริ่ม/ถูกลบ = ผู้มีสิทธิ์ campaign_expired ยังกดแล้วยืนยันได้ (confirmExpired)
     ยังไม่บันทึกแม่แบบ/สาขา = ไฟล์พิมพ์อ่านค่าจากฐาน จะได้แม่แบบเดิมเงียบ ๆ จึงต้องบันทึกก่อน */
  const campaignText = (cp) => {
    if (cp.status === 'DRAFT') return 'แคมเปญปิดใช้งาน';
    if (cp.status === 'EXPIRED') return `แคมเปญหมดอายุแล้ว ${Func.dateDisplay(cp.date_stop)}`;
    if (cp.status === 'SCHEDULED')
      return `แคมเปญยังไม่เริ่ม เริ่ม ${Func.dateDisplay(cp.date_start)}`;
    if (cp.status === 'DELETED') return 'แคมเปญถูกลบแล้ว';
    return 'สถานะแคมเปญเปลี่ยนแล้ว เปิดป้ายนี้ใหม่';
  };
  const campaignBlocked = () => {
    const cp = attr.campaign;
    if (!cp || cp.status === 'ACTIVE') return '';
    if (cp.status !== 'DRAFT' && app.perm && app.perm.campaign_expired) return '';
    return campaignText(cp);
  };
  const unsaved =
    Boolean(saved) &&
    ['branch_id', 'template_id'].some((k) => String(attr[k] ?? '0') !== String(saved[k] ?? '0'));
  const printBlock = campaignBlocked() || (unsaved ? 'ยังไม่บันทึก · กดบันทึกก่อนพิมพ์' : '');
  /* แม่แบบที่บันทึกไว้เป็นป้ายโปรแบบใหม่ไหม (ไฟล์พิมพ์อ่านค่าจากฐาน) */
  const savedTpl = saved && cboTemplate.find((x) => String(x.id) === String(saved.template_id));
  const promoSaved = Boolean(savedTpl && savedTpl.layout === 'PROMO');
  /* ป้ายเก่า (แม่แบบเดิม · ร้านมี ~2,100 ชุด) ไม่มีภาพน้องช้าง AI เพราะภาพใช้กับป้ายโปรแบบใหม่เท่านั้น
     แท็บ "ภาพบนป้าย" จึงขึ้นทุกชุด — ชุดเก่าเจอปุ่มเปลี่ยนเป็นป้ายโปร 7×5 ซม. (เจ้าของงาน 1 พ.ย.: "tag เก่าไม่มีฟังก์ชันเจน AI") */
  const promoTpl = cboTemplate.find((x) => x.code === 'PROMO_7X5_A4' && x.layout === 'PROMO');
  const toPromo = () => {
    Swal.fire({
      title: 'เปลี่ยนชุดนี้เป็นป้ายโปร 7×5 ซม.?',
      html:
        'พิมพ์เป็นหน้าตาใหม่ (รูปสินค้า · น้องช้าง · คำโปร) A4 แผ่นละ 16 ใบ<br>' +
        'ราคาคิดจากราคาสาขาและแคมเปญตอนพิมพ์ ไม่ใช้บรรทัดราคาที่ตั้งไว้ในชุดนี้<br>' +
        'เปลี่ยนกลับได้ที่ช่อง "ขนาดป้าย" แล้วกดบันทึก',
      icon: 'question',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'เปลี่ยนเป็นป้ายโปร',
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (!swal.isConfirmed || !promoTpl) return;
      setLoading(true);
      const next = { ...attr, template_id: String(promoTpl.id) };
      FetchData('PATCH', `/${mod}/${attr.id}`, app.token, {
        branch_id: next.branch_id,
        remark: next.remark,
        template_id: next.template_id,
      })
        .then((result) => {
          setLoading(false);
          if (result.status === 'ok') {
            setAttr(next);
            setSaved(next);
            showNotify('เปลี่ยนเป็นป้ายโปรแล้ว', 'success');
          } else {
            showNotify(t(`alert.saveerror`), 'error');
          }
        })
        .catch(() => {
          setLoading(false);
          showNotify(t(`alert.saveerror`), 'error');
        });
    });
  };

  /* TAG_A4_GRID คือแม่แบบเดียวกับ "ตามค่าตั้งต้น" (tag.print.php DEFAULT_TEMPLATE) ซ่อนไม่ให้ซ้ำ เว้นแต่ป้ายนี้บันทึกไว้แล้ว */
  const templateOptions = (keepId) =>
    cboTemplate.filter((x) => x.code !== 'TAG_A4_GRID' || String(x.id) === String(keepId));

  const askArt = (waiting) =>
    Swal.fire({
      title: `มี ${waiting} ใบที่ภาพรออนุมัติ`,
      text: 'ใบเหล่านี้จะพิมพ์ด้วยรูปสินค้าเดิมไปก่อน',
      icon: 'question',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'พิมพ์เลย',
      denyButtonText: 'ไปอนุมัติก่อน',
      denyButtonColor: '#6c757d',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isDenied) setJump({ kind: 'review', n: Date.now() }); // เปิดตรวจทีละใบที่ใบแรกที่รอ
      return swal.isConfirmed;
    });

  /* ป้ายโปรที่ยังมีภาพรออนุมัติ — ใบนั้นพิมพ์ภาพเดิม/ช้างมาตรฐาน ถามก่อน (ตรวจการใช้งาน 1.6 · 1 พ.ย. 2569) */
  const confirmArt = () => {
    if (!promoSaved) return Promise.resolve(true);
    /* นับแบบเดียวกับแท็บภาพบนป้าย (ภาพช้างของคำโปรที่ใบนั้นใช้) — ยังไม่เปิดแท็บ = ถามรายการก่อน (ผู้ตรวจ 1 พ.ย. 2569) */
    if (!artSum) setLoading(true);
    const count = artSum
      ? Promise.resolve(Number(artSum.waiting || 0))
      : FetchData('OPTIONS', '/tag_art/list', app.token, { tag_id: attr.id, _t: Date.now() })
          .then((r) =>
            r && r.status === 'ok'
              ? (r.items || []).filter(
                  (it) =>
                    r.mascot_fixed === 'Y' // ช้างของแบรนด์ = รอแค่รูปสินค้า AI
                      ? it.photo && it.photo.status === 'WAITING'
                      : (it.art && it.art.status === 'WAITING') ||
                        (it.photo && it.photo.status === 'WAITING') // แบบช้าง AI: นับรูปสินค้า AI ด้วย (แบบแท็บภาพบนป้าย)
                ).length
              : 0
          )
          .catch(() => 0)
          .finally(() => setLoading(false));
    return count.then((waiting) => (waiting < 1 ? true : askArt(waiting)));
  };
  /* ป้ายแบบเดิมพิมพ์ราคาที่เก็บไว้ตอนทำป้าย — ตรวจกับราคาสาขา/โปรวันนี้ก่อน (ตรวจการใช้งาน 2.5 · 1 พ.ย. 2569)
     อัปเดต = ราคาปกติเป็นราคาตอนนี้ + ตัดโปรที่ไม่ใช้แล้ว · สินค้าปิดขาย ไฟล์พิมพ์ข้ามให้เอง */
  const confirmPrice = () => {
    if (promoSaved) return Promise.resolve(true);
    setLoading(true);
    return FetchData('OPTIONS', `/${mod}/price_check`, app.token, { id: attr.id, _t: Date.now() })
      .then((r) => {
        setLoading(false);
        const c = (r && r.status === 'ok' && r.counts) || {};
        const fixable = Number(c.price || 0) + Number(c.promo || 0);
        if (!fixable && !Number(c.closed || 0)) return true;
        const lines = [
          c.price ? `ราคาปกติเปลี่ยน ${c.price} ใบ` : '',
          c.promo ? `โปรที่ไม่ใช้แล้ว ${c.promo} บรรทัด` : '',
          c.closed ? `สินค้าปิดขาย ${c.closed} ใบ (ไม่พิมพ์)` : '',
        ].filter(Boolean);
        return Swal.fire({
          title: 'ราคาบนป้ายไม่ตรงกับวันนี้',
          html: lines.join('<br>'),
          icon: 'warning',
          showDenyButton: Boolean(fixable),
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          confirmButtonText: fixable ? 'อัปเดตราคาแล้วพิมพ์' : 'พิมพ์',
          denyButtonText: 'พิมพ์ตามเดิม',
          denyButtonColor: '#6c757d',
          cancelButtonText: t(`element.cancel`),
        }).then((swal) => {
          if (swal.isDenied) return true;
          if (!swal.isConfirmed) return false;
          if (!fixable) return true;
          setLoading(true);
          return FetchData('OPTIONS', `/${mod}/price_check`, app.token, {
            id: attr.id,
            fix: 'Y',
            _t: Date.now(),
          })
            .then((f) => {
              setLoading(false);
              if (f && f.status === 'ok') {
                showNotify(`อัปเดตราคา ${f.updated} ใบ`, 'success');
                setPriceTick((n) => n + 1); // รายการในชุดโชว์ราคาใหม่ (TagList โหลดใหม่ตาม reloadKey)
                return true;
              }
              showNotify(
                f && f.msg === 'permission_denied'
                  ? 'บัญชีนี้แก้ราคาบนป้ายไม่ได้'
                  : 'อัปเดตราคาไม่สำเร็จ',
                'error'
              );
              return false;
            })
            .catch(() => {
              setLoading(false);
              showNotify('เชื่อมต่อไม่สำเร็จ ลองอีกครั้ง', 'error');
              return false;
            });
        });
      })
      .catch(() => {
        setLoading(false);
        return true; // ตรวจไม่ได้ = พิมพ์ตามเดิม ไม่ขวางงาน
      });
  };

  const print = () => {
    confirmArt()
      .then((go) => go && confirmPrice())
      .then((go) => go && confirmExpired())
      .then((expired) => {
        if (!expired) return;
        setLoading(true);

        FetchData('OPTIONS', `/${mod}/print`, app.token, {
          id: attr.id,
          lang: app.lang,
          include_expired: expired,
        })
          .then((result) => {
            setLoading(false);
            if (result.status === 'ok') {
              setAttr({ ...attr, print: result.data.print, print_no: result.data.print_no });
              setModalPrint(true);
            } else {
              showNotify('พิมพ์ไม่ได้ ลองอีกครั้ง', 'error');
            }
          })
          .catch(() => {
            setLoading(false);
            showNotify('เชื่อมต่อไม่สำเร็จ ลองอีกครั้ง', 'error');
          });
      });
  };

  const sticker = () => {
    confirmArt()
      .then((go) => go && confirmPrice())
      .then((go) => go && confirmExpired())
      .then((expired) => {
        if (!expired) return;
        setLoading(true);

        FetchData('OPTIONS', `/${mod}/sticker`, app.token, {
          id: attr.id,
          lang: app.lang,
          include_expired: expired,
        })
          .then((result) => {
            setLoading(false);
            if (result.status === 'ok') {
              setAttr({ ...attr, print: result.data.print, sticker_no: result.data.sticker_no });
              setModalPrint(true);
            } else {
              showNotify('พิมพ์ไม่ได้ ลองอีกครั้ง', 'error');
            }
          })
          .catch(() => {
            setLoading(false);
            showNotify('เชื่อมต่อไม่สำเร็จ ลองอีกครั้ง', 'error');
          });
      });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setCboBranch(result.branch);
        setCboTemplate(result.template || []); // ค่าตั้งต้น (0) อยู่ใน valueDefault ของ MDSelect แล้ว

        if (id === undefined) {
          view(find);
        } else {
          open();
        }
      } else {
        Func.exitApp();
      }
    });
  };

  const btnOpen = (item) => {
    return (
      <NavLink to={`/${mod}/${item.id}`}>
        <MDButton
          variant='text'
          color='info'
          size='small'
          sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}
        >
          {item.code}
        </MDButton>
      </NavLink>
    );
  };

  const btnDel = (item) => {
    if (!app.perm.del) return null;

    return (
      <Tooltip title={t(`element.delete`)}>
        <MDButton variant='text' color='error' size='small' iconOnly onClick={() => del(item)}>
          <Icon>delete</Icon>
        </MDButton>
      </Tooltip>
    );
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
                placeholder='รหัสชุด · หมายเหตุ · SKU'
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6}>
            <MDInput
              fullWidth
              value={find.branch}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, branch: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('menu.branch')} :</InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              {cboBranch.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          {/* ตัวกรอง แบบป้าย / สถานะ / สร้างเมื่อ (2 พ.ย. 2569 · แบบ Claude Design อาร์ตบอร์ด 1) */}
          {[
            [
              'kind',
              'แบบป้าย',
              [
                ['ALL', 'ทุกแบบ'],
                ['PROMO', 'ป้ายโปร 7×5'],
                ['CLASSIC', 'แบบเดิม'],
              ],
            ],
            [
              'status',
              'สถานะ',
              [
                ['ALL', 'ทั้งหมด'],
                ['WAITING', 'มีรูป AI รออนุมัติ'],
                ['NEVER', 'ยังไม่เคยพิมพ์'],
                ['PRINTED', 'พิมพ์แล้ว'],
              ],
            ],
            [
              'period',
              'สร้างเมื่อ',
              [
                ['ALL', 'ทุกช่วง'],
                ['7', '7 วันล่าสุด'],
                ['30', '30 วันล่าสุด'],
                ['90', '90 วันล่าสุด'],
              ],
            ],
          ].map(([key, label, opts]) => (
            <Grid item xs={12} sm={4} key={key}>
              <MDInput
                fullWidth
                select
                value={find[key]}
                onChange={(e) => view({ ...find, [key]: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: <InputAdornment position='start'>{label} :</InputAdornment>,
                }}
              >
                {opts.map(([v, text]) => (
                  <MenuItem key={v} value={v}>
                    {text}
                  </MenuItem>
                ))}
              </MDInput>
            </Grid>
          ))}
        </Grid>
      </Card>
    );
  };

  /* สถานะของชุดในตาราง ใช้คำเดียวกับตัวกรอง: รูป AI รออนุมัติ → ยังไม่มีสินค้า → ยังไม่เคยพิมพ์ → พิมพ์แล้ว */
  const rowStatus = (row) => {
    if (Number(row.art_waiting) > 0)
      return { label: `รูป AI รออนุมัติ ${row.art_waiting}`, color: 'warning' };
    if (!Number(row.count)) return { label: 'ยังไม่มีสินค้า', color: 'default' };
    if (!Number(row.print_no) && !Number(row.sticker_no))
      return { label: 'ยังไม่เคยพิมพ์', color: 'info' };
    return { label: 'พิมพ์แล้ว', color: 'success' };
  };

  /* ── หลายชุดพร้อมกัน (ตรวจการใช้งาน 2.4) ── */
  const MULTI_ERR = {
    tag_multi_promo_only:
      'พิมพ์รวมได้เฉพาะป้ายโปร 7×5 — ชุดแบบเดิมพิมพ์ทีละชุด หรือเปลี่ยนเป็นป้ายโปรก่อน',
    tag_multi_mixed: 'พิมพ์รวมได้เฉพาะชุดสาขาเดียวกัน ขนาดป้ายเดียวกัน และแคมเปญเดียวกัน',
    tag_not_found: 'มีชุดที่ถูกลบไปแล้ว — กดล้างที่เลือกแล้วเลือกใหม่',
    tag_campaign_outside: 'แคมเปญของชุดที่เลือกหมดอายุหรือยังไม่เริ่ม — เปิดพิมพ์ทีละชุด',
    tag_multi_too_many: 'เลือกได้ไม่เกิน 30 ชุดต่อครั้ง',
    tag_multi_need_two: 'เลือกอย่างน้อย 2 ชุด',
  };
  /* ชุด 1 ของไฟล์/หมวดได้รหัสสูงสุด → เรียง id มากไปน้อย = ชุด 1, 2, 3 … */
  const pickedList = Object.values(pickedTags).sort((a, b) => Number(b.id) - Number(a.id));
  const pickedPromo = pickedList.filter((r) => r.layout === 'PROMO');
  const pickedClassic = pickedList.filter((r) => r.layout !== 'PROMO');
  const seqRun = (list, fn) =>
    list.reduce(
      (p, row) =>
        p.then((acc) =>
          fn(row)
            .then((r) => [...acc, r])
            .catch(() => [...acc, null])
        ),
      Promise.resolve([])
    );

  const printMulti = async (mode) => {
    const waiting = pickedList.reduce((n, r) => n + Number(r.art_waiting || 0), 0);
    if (waiting > 0 && !(await askArt(waiting))) return;
    setLoading(true);
    FetchData('OPTIONS', `/${mod}/print_multi`, app.token, {
      ids: pickedList.map((r) => r.id),
      mode,
      _t: Date.now(),
    })
      .then((r) => {
        setLoading(false);
        if (r && r.status === 'ok') {
          setAttr({ ...attr, code: `รวม ${pickedList.length} ชุด`, print: r.data.print });
          setModalPrint(true);
          view(find);
        } else if (r && r.msg === 'tag_multi_too_many_items')
          showNotify(
            `พิมพ์รวมได้ครั้งละไม่เกิน ${Func.numberDisplay(
              r.max,
              0
            )} ใบ (เลือกไว้ ${Func.numberDisplay(r.count, 0)} ใบ) — เลือกชุดน้อยลงแล้วพิมพ์ทีละรอบ`,
            'error'
          );
        else showNotify(MULTI_ERR[r && r.msg] || 'พิมพ์ไม่ได้ ลองอีกครั้ง', 'error');
      })
      .catch(() => {
        setLoading(false);
        showNotify('เชื่อมต่อไม่สำเร็จ ลองอีกครั้ง', 'error');
      });
  };

  const toPromoMulti = () => {
    if (!promoTpl || !pickedClassic.length) return;
    Swal.fire({
      title: `เปลี่ยน ${pickedClassic.length} ชุดเป็นป้ายโปร 7×5 ซม.?`,
      html: 'ราคาคิดจากราคาสาขาและแคมเปญตอนพิมพ์ · เปลี่ยนกลับได้ทีละชุดที่ช่อง "ขนาดป้าย"',
      icon: 'question',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'เปลี่ยนเป็นป้ายโปร',
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (!swal.isConfirmed) return;
      setLoading(true);
      seqRun(pickedClassic, (row) =>
        FetchData('PATCH', `/${mod}/${row.id}`, app.token, {
          branch_id: row.branch_id,
          remark: row.remark,
          template_id: String(promoTpl.id),
        })
      ).then((res) => {
        setLoading(false);
        const ok = res.filter((r) => r && r.status === 'ok').length;
        showNotify(
          `เปลี่ยนเป็นป้ายโปร ${ok} ชุด${ok < res.length ? ` · ไม่สำเร็จ ${res.length - ok}` : ''}`,
          ok < res.length ? 'warning' : 'success'
        );
        setPickedTags({});
        view(find);
      });
    });
  };

  const genMulti = () => {
    if (!pickedPromo.length) return;
    setLoading(true);
    /* ช้างบนป้ายเป็นช้างของแบรนด์ (พี่นก 1 พ.ย.) — ทำได้เฉพาะรูปสินค้า AI */
    const body = (row, dry) => ({
      tag_id: row.id,
      photo: 'Y',
      ...(dry ? { dry: 'Y' } : {}),
      _t: Date.now(),
    });
    seqRun(pickedPromo, (row) =>
      FetchData('OPTIONS', '/tag_art/generate', app.token, body(row, true))
    ).then((dry) => {
      setLoading(false);
      const count = dry.reduce((n, r) => n + Number((r && r.count) || 0), 0);
      const credits = dry.reduce((n, r) => n + Number((r && r.credits) || 0), 0);
      if (!count) {
        const why = dry.find((r) => r && r.status !== 'ok' && r.msg !== 'nothing_to_generate');
        const noImage = dry.reduce((n, r) => n + Number((r && r.no_image) || 0), 0);
        const noPrice = dry.reduce((n, r) => n + Number((r && r.no_price) || 0), 0);
        let msg = 'ทุกสินค้ามีภาพแล้ว หรือกำลังทำอยู่';
        if (dry.some((r) => !r)) msg = 'เชื่อมต่อไม่สำเร็จ ลองอีกครั้ง';
        else if (why) msg = tagArtErrorText(why.msg);
        else if (noImage || noPrice)
          msg = `ไม่มีสินค้าที่ทำได้ · ไม่มีรูป ${noImage} · ไม่มีราคา ${noPrice}`;
        showNotify(msg, 'warning');
        return;
      }
      Swal.fire({
        title: `ทำรูปสินค้า AI ${count} สินค้า?`,
        html: `≈ ${Func.numberDisplay(credits, 0)} เครดิต · ${
          pickedPromo.length
        } ชุด · เสร็จแล้วรออนุมัติที่หน้าชุดป้าย หรือหน้างาน AI ของฉัน`,
        icon: 'question',
        showDenyButton: true,
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'สั่งทำภาพ',
        denyButtonText: t(`element.cancel`),
        denyButtonColor: '#dc3545',
        reverseButtons: true,
      }).then((swal) => {
        if (!swal.isConfirmed) return;
        setLoading(true);
        seqRun(pickedPromo, (row) =>
          FetchData('OPTIONS', '/tag_art/generate', app.token, body(row, false))
        ).then((res) => {
          setLoading(false);
          const queued = res.reduce(
            (n, r) => n + Number((r && r.status === 'ok' && r.count) || 0),
            0
          );
          const failed = (r) => !r || (r.status !== 'ok' && r.msg !== 'nothing_to_generate');
          const fail = res.filter(failed);
          const firstFail = fail.find(Boolean);
          let failText = '';
          if (fail.length)
            failText = ` · ไม่สำเร็จ ${fail.length} ชุด: ${
              firstFail ? tagArtErrorText(firstFail.msg) : 'เชื่อมต่อไม่สำเร็จ'
            }`;
          showNotify(
            `สั่งทำรูปสินค้า AI ${queued} สินค้า${failText}`,
            fail.length ? 'warning' : 'success'
          );
          /* ชุดที่ล้มยังเลือกไว้ให้กดซ้ำ */
          setPickedTags((cur) => {
            const next = {};
            pickedPromo.forEach((row, i) => {
              if (failed(res[i]) && cur[row.id]) next[row.id] = cur[row.id];
            });
            return next;
          });
        });
      });
    });
  };

  const renderBulk = () => (
    <Card
      sx={{
        p: 1.5,
        display: 'flex',
        flexDirection: 'row', // Card ของ MUI เป็นแนวตั้งเป็นค่าตั้งต้น
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
      }}
    >
      <MDTypography variant='button' fontWeight='medium' sx={{ mr: 1 }}>
        เลือก {pickedList.length} ชุด
      </MDTypography>
      <MDButton
        size='small'
        color='info'
        disabled={pickedList.length < 2}
        title={pickedList.length < 2 ? 'เลือกอย่างน้อย 2 ชุด' : ''}
        onClick={() => printMulti('print')}
      >
        <Icon>print</Icon>&nbsp;พิมพ์รวม (A4)
      </MDButton>
      <MDButton
        size='small'
        color='info'
        disabled={pickedList.length < 2}
        title={pickedList.length < 2 ? 'เลือกอย่างน้อย 2 ชุด' : ''}
        onClick={() => printMulti('sticker')}
      >
        <Icon>print</Icon>&nbsp;STICKER รวม
      </MDButton>
      {promoTpl && pickedClassic.length ? (
        <MDButton size='small' variant='outlined' color='info' onClick={toPromoMulti}>
          <Icon>auto_awesome</Icon>&nbsp;เปลี่ยนเป็นป้ายโปร ({pickedClassic.length})
        </MDButton>
      ) : null}
      {pickedPromo.length && app.perm && app.perm.ai_generate ? (
        <MDButton size='small' variant='outlined' color='info' onClick={genMulti}>
          <Icon>photo_camera</Icon>&nbsp;รูปสินค้า AI ที่ยังไม่มี ({pickedPromo.length} ชุด)
        </MDButton>
      ) : null}
      <MDButton size='small' variant='text' color='dark' onClick={() => setPickedTags({})}>
        ล้างที่เลือก
      </MDButton>
    </Card>
  );

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={7} lg={8}>
            {renderSearch()}
          </Grid>
          <Grid item xs={6} sm={3} md={3} lg={2}>
            <Tooltip title='สร้างป้ายจากแคมเปญ'>
              <MDButton
                variant='outlined'
                color='dark'
                size='large'
                fullWidth
                sx={{ pt: 1.6, pb: 1.6, whiteSpace: 'nowrap' }}
                onClick={() => setModalCampaign(true)}
              >
                <Icon>campaign</Icon>&nbsp;จากแคมเปญ
              </MDButton>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={3} md={2} lg={2}>
            <Tooltip title='สร้างชุดป้ายใหม่ (ตามยอดขาย / อัปไฟล์รหัสสินค้า / ชุดเปล่า)'>
              <MDButton
                variant='contained'
                color='dark'
                size='large'
                fullWidth
                aria-label='สร้างชุดป้ายใหม่'
                sx={{ pt: 1.6, pb: 1.6, whiteSpace: 'nowrap' }}
                onClick={() => {
                  /* ชุดใหม่เริ่มเป็นป้ายโปร 7×5 (ตรวจการใช้งาน 1.4 · 1 พ.ย. 2569) — ไม่มีแม่แบบนี้ = ค่าตั้งต้นเดิม */
                  setAttr(promoTpl ? { ...initAttr, template_id: String(promoTpl.id) } : initAttr);
                  setConfirm(false);
                  setRank(RANK_EMPTY);
                  setArtAuto(true);
                  setAddStep(1);
                  setAddSource('rank');
                  setRankStatus({ active: false, reason: '' });
                  setModalAdd(true);
                  FetchData('OPTIONS', '/tag_art/meta', app.token, { _t: Date.now() })
                    .then((r) => setArtMeta(r && r.status === 'ok' ? r : null))
                    .catch(() => setArtMeta(null));
                }}
              >
                <Icon>post_add</Icon>&nbsp;สร้างชุดป้ายใหม่
              </MDButton>
            </Tooltip>
          </Grid>
          <TagFromCampaign
            open={modalCampaign}
            onClose={() => setModalCampaign(false)}
            app={app}
            branches={cboBranch}
            templates={templateOptions()}
            onError={(msg) => showNotify(msg, 'error')}
            onCreated={(result) => {
              setModalCampaign(false);
              showNotify(`สร้างป้าย ${result.code} ${result.count} รายการ`, 'success');
              /* เปลี่ยนหน้าด้วย router ไม่โหลดหน้าใหม่ ข้อความสำเร็จจึงยังอยู่ (18 ต.ค. 2569) */
              routerNavigate(`/${mod}/${result.id}`);
            }}
          />
          {Object.keys(pickedTags).length ? (
            <Grid item xs={12}>
              {renderBulk()}
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='simple table'>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover selected={Boolean(pickedTags[row.id])}>
                      <TableCell align='center' sx={{ whiteSpace: 'nowrap' }}>
                        <Checkbox
                          size='small'
                          checked={Boolean(pickedTags[row.id])}
                          onChange={() =>
                            setPickedTags((cur) => {
                              const next = { ...cur };
                              if (next[row.id]) delete next[row.id];
                              else next[row.id] = row;
                              return next;
                            })
                          }
                          inputProps={{ 'aria-label': `เลือก ${row.code}` }}
                          sx={{ p: 0.5 }}
                        />
                        {row.id}
                      </TableCell>
                      <TableCell align='center'>
                        {btnOpen(row)}
                        {/* แบบป้าย + ภาพรออนุมัติ — รู้จากตารางว่าชุดไหนแบบไหน ชุดไหนมีงานค้าง (ตรวจการใช้งาน 1.10 · 1 พ.ย. 2569) */}
                        {row.layout ? (
                          <MDTypography
                            variant='caption'
                            color='text'
                            display='block'
                            lineHeight={1}
                          >
                            {row.layout === 'PROMO' ? 'ป้ายโปร 7×5' : 'แบบเดิม'}
                          </MDTypography>
                        ) : null}
                      </TableCell>
                      <TableCell align='left'>{row.branch_name}</TableCell>
                      <TableCell align='left'>{row.remark}</TableCell>
                      <TableCell align='left' sx={{ whiteSpace: 'nowrap' }}>
                        {Number(row.count).toLocaleString('th-TH')} ป้าย
                        {Number(row.sheets) > 0 ? (
                          <MDTypography
                            variant='caption'
                            color='text'
                            display='block'
                            lineHeight={1.2}
                          >
                            ≈ {row.sheets} แผ่น ({row.per_sheet} ใบ/แผ่น)
                          </MDTypography>
                        ) : null}
                      </TableCell>
                      <TableCell align='left'>
                        <Chip size='small' {...rowStatus(row)} />
                      </TableCell>
                      <TableCell align='left'>{row.emp_name}</TableCell>
                      <TableCell align='center'>{Func.dateTimeDisplay(row.created_at)}</TableCell>
                      <TableCell align='center' sx={{ whiteSpace: 'nowrap' }}>
                        <NavLink to={`/${mod}/${row.id}`}>
                          <MDButton size='small' variant='outlined' color='dark'>
                            เปิด
                          </MDButton>
                        </NavLink>
                        {btnDel(row)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  /* row = วางเป็นแถวเดียว 3 ช่อง (การ์ด "ตั้งค่าทั้งชุด" ของหน้าชุดป้ายโปร · 2 พ.ย. 2569) */
  const renderInfo = (row = false) => {
    const col = row ? { xs: 12, md: 4 } : { xs: 12 };
    return (
      <Grid container spacing={2}>
        {/* เลือกขนาดป้ายก่อน แล้วดูแผ่นตัวอย่างว่าได้กี่ใบ (29 ต.ค. 2569 · flow ที่คุยกับพี่นก) */}
        <Grid item {...col}>
          <MDSelect
            label='ขนาดป้าย (แม่แบบพิมพ์)'
            labelDefault='ตามค่าตั้งต้น (A4 แนวนอน 16 ดวง)'
            valueDefault='0'
            data={templateOptions(attr.id ? saved && saved.template_id : '')}
            value={String(attr.template_id ?? '0')}
            onChange={(e) => setAttr({ ...attr, template_id: e.target.value })}
          />
          <SheetPreview tpl={addTpl || null} />
          {!attr.id && addTpl && addTpl.layout === 'PROMO' ? (
            <MDInput
              select
              variant='standard'
              label='ราคาบนป้าย'
              value={newLevels}
              onChange={(e) => {
                setNewLevels(e.target.value);
                try {
                  localStorage.setItem('tag.price_levels', e.target.value);
                } catch (err) {
                  /* โหมดส่วนตัว — ไม่จำค่าก็ได้ */
                }
              }}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{ mt: 1.5 }}
            >
              <MenuItem value='1'>ราคาโปรอย่างเดียว · ไม่มี 1 ชิ้น</MenuItem>
              <MenuItem value='2'>2 ราคา · 1 ชิ้น + โปร</MenuItem>
              <MenuItem value='3'>3 ราคา · ทุกขั้นโปร</MenuItem>
            </MDInput>
          ) : null}
          {!attr.id && !(addTpl && addTpl.layout === 'PROMO') && (rank.cat_id || rank.file) ? (
            <MDTypography variant='caption' color='warning' display='block' mt={1}>
              ป้ายแบบเดิมจากหมวด/ไฟล์ พิมพ์ราคาปกติอย่างเดียว ไม่มีราคาโปร · ต้องการราคาโปร เลือก
              &quot;ป้ายโปร 7×5&quot;
            </MDTypography>
          ) : null}
        </Grid>
        <Grid item {...col}>
          <MDSelect
            label={row ? 'สาขา · ราคาและโปรของสาขานี้' : 'สาขา'}
            labelDefault={t(`element.please_select`)}
            data={cboBranch}
            value={attr.branch_id}
            onChange={(e) => setAttr({ ...attr, branch_id: e.target.value })}
            inputProps={{
              autoComplete: 'off',
            }}
          />
        </Grid>
        <Grid item {...col}>
          <MDInput
            label={t(`attr.remark`)}
            onChange={(e) => setAttr({ ...attr, remark: e.target.value })}
            value={attr.remark}
            variant='standard'
            multiline
            inputProps={{
              maxLength: 200,
            }}
            fullWidth
          />
        </Grid>
      </Grid>
    );
  };

  /* หน้าต่างสร้างชุดป้ายใหม่ 3 ขั้น (2 พ.ย. 2569 · อาร์ตบอร์ด 5 ของแบบ Claude Design):
     ① ตั้งค่าชุด (ขนาด · สาขา · หมายเหตุ · ราคาบนป้าย) ② สินค้ามาจากไหน (ตามยอดขาย / จากไฟล์ / จากแคมเปญ / ชุดเปล่า) + แผงสรุปสด
     ③ ตรวจก่อนสร้าง (สรุปทุกอย่าง + สวิตช์ทำภาพ) → "สร้างป้าย" กดครั้งเดียว (เดิมบันทึก → ยืนยันบันทึก 2 จังหวะ) */
  const addBranchName = (cboBranch.find((x) => String(x.id) === String(attr.branch_id)) || {}).name;
  const addPromo = Boolean(addTpl && addTpl.layout === 'PROMO');
  const addPerPage = perPageOf(addTpl);
  /* ชุดละเต็มแผ่น (เท่า tag_rank.con.php MAX_PER_TAG 200 ปัดลงเป็นแผ่นเต็ม) */
  const addPerSet = Math.max(addPerPage, Math.floor(200 / addPerPage) * addPerPage);
  const addTotal = addSource === 'empty' ? 0 : Number(rankStatus.total || 0);
  const addPages = (() => {
    let n = 0;
    for (let left = addTotal; left > 0; left -= addPerSet)
      n += Math.ceil(Math.min(left, addPerSet) / addPerPage);
    return n;
  })();
  const addSets = Math.ceil(addTotal / addPerSet);
  const addCredits =
    artOn && artAuto ? addTotal * Number((artMeta && artMeta.credits_each) || 4) : 0;
  const addFile = rank.file ? fileMerge(rank.file) : null;
  const addSkipped = addFile
    ? { missing: addFile.missingCount, closed: addFile.closedCount }
    : null;
  const addReady =
    addSource === 'empty' ||
    (rankStatus.active && !rankStatus.reason && !rankStatus.busy && addTotal > 0);
  const addStepReason = () => {
    if (addStep === 1) return Func.verifyLength(attr.branch_id, 1) ? '' : 'เลือกสาขาก่อน';
    if (addStep === 2) {
      if (addSource === 'empty') return '';
      if (addSource === 'rank' && !rank.cat_id) return 'เลือกหมวดก่อน';
      if (addSource === 'file' && !rank.file && !rankStatus.busy) return 'อัปไฟล์รหัสสินค้าก่อน';
      return rankStatus.reason || (addTotal > 0 ? '' : 'ยังไม่มีสินค้า');
    }
    return '';
  };
  const pickSource = (src) => {
    if (rankStatus.busy) return; // กำลังอ่านไฟล์ — ไม่สลับทาง ไม่งั้นผลอ่านไฟล์ค้างในทางอื่น
    if (src === 'campaign') {
      setModalAdd(false);
      setModalCampaign(true);
      return;
    }
    setAddSource(src);
    if (src === 'rank') setRank({ ...RANK_EMPTY, ...rank, file: null, cat_id: rank.cat_id || '' });
    else if (src === 'file') setRank({ ...RANK_EMPTY, file: rank.file });
    else setRank(RANK_EMPTY);
    setArtAuto(src !== 'file'); // ไฟล์สาขาใหม่หลักพันรายการ — ไม่เปิดทำภาพช้างให้เอง (สินค้าละ 6 เครดิต)
  };
  const sourceCard = (src, icon, title, text) => {
    const on = addSource === src;
    return (
      <Grid item xs={12} sm={6} key={src}>
        <MDBox
          component='button'
          type='button'
          onClick={() => pickSource(src)}
          aria-pressed={on}
          display='flex'
          alignItems='flex-start'
          gap={1.25}
          p={1.5}
          sx={{
            width: '100%',
            height: '100%',
            textAlign: 'left',
            whiteSpace: 'normal !important', // แอปตั้ง button { nowrap !important } ทั้งระบบ
            cursor: 'pointer',
            font: 'inherit',
            color: '#344767',
            bgcolor: on ? '#f5f9ff' : '#fff',
            border: on ? '2px solid #1A73E8' : '1px solid #dee2e6',
            borderRadius: 2.5,
          }}
        >
          <MDBox
            sx={{
              width: 36,
              height: 36,
              flex: 'none',
              borderRadius: 2,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: on ? '#1A73E8' : '#f0f2f5',
              color: on ? '#fff' : '#495057',
            }}
          >
            <Icon>{icon}</Icon>
          </MDBox>
          <MDBox sx={{ minWidth: 0, flex: 1 }}>
            <MDTypography variant='button' fontWeight='bold' display='block'>
              {title}
            </MDTypography>
            <MDTypography variant='caption' color='text' display='block' lineHeight={1.45}>
              {text}
            </MDTypography>
          </MDBox>
        </MDBox>
      </Grid>
    );
  };
  const stepHead = (n, title, sub) => {
    const done = addStep > n;
    const cur = addStep === n;
    return (
      <MDBox
        component='li'
        display='flex'
        alignItems='center'
        gap={1.25}
        py={1.25}
        sx={{
          flex: 1,
          minWidth: 0,
          borderBottom: `3px solid ${done ? '#4CAF50' : cur ? '#1A73E8' : '#f0f2f5'}`,
          opacity: done || cur ? 1 : 0.6,
        }}
        aria-current={cur ? 'step' : undefined}
      >
        <MDBox
          sx={{
            width: 26,
            height: 26,
            borderRadius: '50%',
            flex: 'none',
            bgcolor: done ? '#4CAF50' : cur ? '#1A73E8' : '#dee2e6',
            color: done || cur ? '#fff' : '#495057',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {done ? '✓' : n}
        </MDBox>
        <MDBox sx={{ minWidth: 0 }}>
          <MDTypography variant='button' fontWeight='bold' display='block'>
            {title}
          </MDTypography>
          <MDTypography variant='caption' color='text' display='block' noWrap>
            {sub}
          </MDTypography>
        </MDBox>
      </MDBox>
    );
  };
  const addSummaryRows = () => {
    const rows = [
      ['ขนาดป้าย', addTpl ? addTpl.name || addTpl.code : 'ตามค่าตั้งต้น (A4 แนวนอน 16 ดวง)'],
      ['สาขา', addBranchName ? `${addBranchName} · ราคาและโปรของสาขานี้` : '-'],
      addPromo
        ? [
            'ราคาบนป้าย',
            { 1: 'ราคาโปรอย่างเดียว', 2: '2 ราคา · 1 ชิ้น + โปร', 3: '3 ราคา · ทุกขั้นโปร' }[
              newLevels
            ] || '2 ราคา',
          ]
        : null,
      ['หมายเหตุ', attr.remark || '-'],
      [
        'สินค้า',
        addSource === 'empty'
          ? 'ชุดเปล่า · ค่อยเพิ่มทีละตัวหรือสแกนบาร์โค้ด'
          : addSource === 'file'
          ? `จากไฟล์ ${rank.file ? rank.file.name : ''} · ป้ายเรียงตามแถวในไฟล์`
          : `ตามยอดขาย${
              rankStatus.limit ? ` · ขายดีสุด ${rankStatus.limit} ตัวแรก` : ''
            } · เกรด ${rank.abc.join('/')} · ${rank.months} เดือน · ขายดีสุดอยู่ช่องแรก`,
      ],
      addSource === 'empty'
        ? null
        : [
            'จะได้',
            `${addTotal.toLocaleString('th-TH')} ใบ · ${addPages.toLocaleString(
              'th-TH'
            )} แผ่น · ${addSets.toLocaleString('th-TH')} ชุด${
              addSets > 1
                ? ` (ชุดละไม่เกิน ${addPerSet / addPerPage} แผ่น · ตั้งชื่อ "ชุด k/n" ให้เอง)`
                : ''
            }`,
          ],
      addSkipped && (addSkipped.missing || addSkipped.closed)
        ? [
            'ข้าม',
            [
              addSkipped.missing ? `ไม่พบในระบบ ${addSkipped.missing}` : '',
              addSkipped.closed ? `ปิดขาย ${addSkipped.closed}` : '',
            ]
              .filter(Boolean)
              .join(' · '),
          ]
        : null,
      [
        'เครดิต',
        addCredits
          ? `ไม่เกิน ${addCredits.toLocaleString('th-TH')} เครดิต (ทำภาพช้าง + คำโปร)`
          : 'ไม่ใช้ · รูป AI สั่งทีหลังได้ทีละใบหรือทั้งชุด',
      ],
    ].filter(Boolean);
    return (
      <MDBox
        component='dl'
        sx={{ m: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 14px' }}
      >
        {rows.map(([k, v]) => (
          <React.Fragment key={k}>
            <MDTypography component='dt' variant='caption' color='text'>
              {k}
            </MDTypography>
            <MDTypography component='dd' variant='button' sx={{ m: 0 }}>
              {v}
            </MDTypography>
          </React.Fragment>
        ))}
      </MDBox>
    );
  };
  const renderAdd = () => {
    const reason = addStepReason();
    return (
      <Dialog
        open={modalAdd}
        maxWidth='md'
        keepMounted
        aria-labelledby='dialog-add-title'
        fullWidth
      >
        <DialogTitle id='dialog-add-title' sx={{ pb: 0 }}>
          สร้างชุดป้ายใหม่
          <IconButton
            aria-label='close'
            onClick={() => setModalAdd(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
          <MDBox
            component='ol'
            display='flex'
            gap={2}
            mt={1.5}
            sx={{ listStyle: 'none', m: 0, p: 0 }}
          >
            {stepHead(
              1,
              'ตั้งค่าชุด',
              addStep > 1
                ? [
                    addTpl ? addTpl.name || addTpl.code : 'ตามค่าตั้งต้น',
                    addBranchName,
                    addPromo ? { 1: 'ราคาโปรอย่างเดียว', 2: '2 ราคา', 3: '3 ราคา' }[newLevels] : '',
                  ]
                    .filter(Boolean)
                    .join(' · ')
                : 'ขนาด · สาขา · หมายเหตุ'
            )}
            {stepHead(
              2,
              'เลือกสินค้า',
              addStep > 2
                ? addSource === 'empty'
                  ? 'ชุดเปล่า'
                  : `${addSource === 'file' ? 'จากไฟล์' : 'ตามยอดขาย'} · ${addTotal.toLocaleString(
                      'th-TH'
                    )} ใบ`
                : 'มาจากไหน กี่ใบ'
            )}
            {stepHead(3, 'ตรวจก่อนสร้าง', 'สรุป · ที่ข้าม · แบ่งชุด')}
          </MDBox>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {addStep === 1 ? <MDBox p={3}>{renderInfo()}</MDBox> : null}
          {addStep === 2 ? (
            <MDBox display='flex' flexWrap='wrap'>
              <MDBox p={3} sx={{ flex: '999 1 420px', minWidth: 0 }}>
                <MDTypography variant='button' fontWeight='bold' display='block' mb={2}>
                  สินค้ามาจากไหน
                </MDTypography>
                <Grid container spacing={1.5} mb={2.5}>
                  {sourceCard(
                    'rank',
                    'trending_up',
                    'ตามยอดขาย',
                    'เลือกหมวดและเกรด A/B/C ระบบหยิบตัวขายดีของสาขาให้'
                  )}
                  {sourceCard(
                    'file',
                    'upload_file',
                    'จากไฟล์รหัสสินค้า',
                    'Excel หรือ CSV ไม่เกิน 3,000 สินค้า · ป้ายเรียงตามไฟล์'
                  )}
                  {sourceCard(
                    'campaign',
                    'campaign',
                    'จากแคมเปญ',
                    'สินค้าทุกตัวในโปร พร้อมราคาโปร'
                  )}
                  {sourceCard(
                    'empty',
                    'add_box',
                    'ชุดเปล่า',
                    'ค่อยเพิ่มทีละตัว หรือสแกนบาร์โค้ดทีหลัง'
                  )}
                </Grid>
                <TagRankPicker
                  open={modalAdd && addStep === 2}
                  mode={addSource}
                  token={app.token}
                  branchId={attr.branch_id}
                  templates={cboTemplate}
                  templateId={String(attr.template_id ?? '0')}
                  value={rank}
                  onChange={(v) => {
                    setRank(v);
                    setConfirm(false);
                  }}
                  onStatus={setRankStatus}
                />
              </MDBox>
              <MDBox
                component='aside'
                aria-label='สรุป'
                p={3}
                sx={{
                  flex: '1 1 260px',
                  bgcolor: '#f8f9fa',
                  borderLeft: '1px solid #f0f2f5',
                }}
                display='flex'
                flexDirection='column'
                gap={1.5}
              >
                <MDTypography variant='caption' color='text' fontWeight='bold'>
                  จะได้อะไร
                </MDTypography>
                {addSource === 'empty' ? (
                  <MDTypography variant='h5'>ชุดเปล่า</MDTypography>
                ) : rankStatus.busy ||
                  (addSource === 'rank' &&
                    rank.cat_id &&
                    !rankStatus.total &&
                    !rankStatus.reason) ? (
                  <MDBox display='flex' alignItems='center' gap={1}>
                    <CircularProgress size={16} />
                    <MDTypography variant='button' color='text'>
                      กำลังนับ…
                    </MDTypography>
                  </MDBox>
                ) : (
                  <MDBox>
                    <MDTypography variant='h3' lineHeight={1}>
                      {addTotal.toLocaleString('th-TH')} ใบ
                    </MDTypography>
                    <MDTypography variant='button' color='text'>
                      {addPages.toLocaleString('th-TH')} แผ่น {addTpl ? addTpl.paper || '' : 'A4'} ·{' '}
                      {addSets.toLocaleString('th-TH')} ชุด
                    </MDTypography>
                  </MDBox>
                )}
                {reason && addSource !== 'empty' ? (
                  <MDTypography variant='caption' color='warning' role='status'>
                    {reason}
                  </MDTypography>
                ) : null}
                <MDBox sx={{ height: '1px', flex: 'none', bgcolor: '#dee2e6' }} />
                <MDTypography variant='caption' color='text'>
                  สาขา {addBranchName || '-'} · ราคาและโปรของสาขานี้
                </MDTypography>
                <MDTypography variant='caption' color='text'>
                  {addSource === 'file'
                    ? 'เรียงตามแถวในไฟล์'
                    : addSource === 'rank'
                    ? 'ขายดีสุดอยู่ช่องแรก'
                    : 'เพิ่มสินค้าได้ที่หน้าชุดป้าย'}
                </MDTypography>
                <MDTypography variant='caption' color='text'>
                  เครดิต ·{' '}
                  {addCredits
                    ? `ไม่เกิน ${addCredits.toLocaleString('th-TH')} (เลือกปิดได้ขั้นถัดไป)`
                    : 'ไม่ใช้ · รูป AI สั่งทีหลังได้'}
                </MDTypography>
                <MDTypography variant='caption' color='text' mt='auto'>
                  เกิน {addPerSet} ใบ ระบบแบ่งเป็นหลายชุดให้ (ชุดละ {addPerSet / addPerPage} แผ่น)
                  และตั้งชื่อ &quot;ชุด k/n&quot; ให้เอง
                </MDTypography>
              </MDBox>
            </MDBox>
          ) : null}
          {addStep === 3 ? (
            <MDBox p={3} display='flex' flexDirection='column' gap={2}>
              {addSummaryRows()}
              {artOn && rankStatus.active && !rankStatus.busy ? (
                <MDBox pt={1} sx={{ borderTop: '1px solid #f0f2f5' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={artAuto}
                        onChange={(e) => setArtAuto(e.target.checked)}
                        inputProps={{
                          'aria-label': 'ทำภาพน้องช้างกับคำโปรให้สินค้าที่ยังไม่มีภาพของคำโปรนั้น',
                        }}
                      />
                    }
                    label={
                      <MDTypography variant='button' color='text'>
                        สร้างแล้วให้ AI วาดช้าง + คำโปรให้สินค้าที่ยังไม่มีภาพของคำโปรนั้นเลย
                      </MDTypography>
                    }
                  />
                  <MDTypography variant='caption' color='text' display='block'>
                    {artAuto
                      ? `ไม่เกิน ${addCredits.toLocaleString('th-TH')} เครดิต (สินค้าละ ${
                          artMeta.credits_each
                        }) · ภาพของคำโปรเดิมที่มีแล้วไม่คิดซ้ำ · เสร็จแล้วรออนุมัติที่หน้าชุดป้าย`
                      : 'ไม่ทำตอนนี้ · สั่งทีหลังได้ที่หน้าชุดป้าย (ทำให้ทุกใบ)'}
                  </MDTypography>
                </MDBox>
              ) : null}
            </MDBox>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <MDButton variant='text' color='dark' onClick={() => setModalAdd(false)}>
            {t(`element.cancel`)}
          </MDButton>
          <MDBox display='flex' alignItems='center' gap={1} flexWrap='wrap'>
            {reason ? (
              <MDTypography variant='caption' color='warning'>
                {reason}
              </MDTypography>
            ) : null}
            {addStep > 1 ? (
              <MDButton
                variant='outlined'
                color='dark'
                disabled={rankStatus.busy}
                onClick={() => setAddStep(addStep - 1)}
              >
                <Icon>chevron_left</Icon>&nbsp;ย้อนกลับ
              </MDButton>
            ) : null}
            {addStep < 3 ? (
              <MDButton
                color='info'
                disabled={Boolean(reason) || (addStep === 2 && rankStatus.busy)}
                onClick={() => setAddStep(addStep + 1)}
              >
                {addStep === 2 ? 'ถัดไป · ตรวจก่อนสร้าง' : 'ถัดไป · เลือกสินค้า'}&nbsp;
                <Icon>chevron_right</Icon>
              </MDButton>
            ) : (
              <MDButton color='success' disabled={!addReady || loading} onClick={add}>
                <Icon>save</Icon>&nbsp;สร้างป้าย
                {addSource !== 'empty' ? ` ${addTotal.toLocaleString('th-TH')} ใบ` : ''}
                {addSets > 1 ? ` · ${addSets} ชุด` : ''}
              </MDButton>
            )}
          </MDBox>
        </DialogActions>
      </Dialog>
    );
  };

  const renderDelete = () => {
    return (
      <Dialog
        open={modalDel}
        onClose={() => setModalDel(false)}
        maxWidth='sm'
        keepMounted
        aria-describedby='dialog-delete'
      >
        <DialogTitle>
          {t(`element.delete`)} #{attr.id}
          <IconButton
            aria-label='close'
            onClick={() => setModalDel(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText p={5}>{t(`alert.confirm_delete_title`)}</DialogContentText>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setModalDel(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='gradient' color='error' onClick={del}>
                  <Icon>delete</Icon>&nbsp;{t(`element.deleteconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant='text' color='error' onClick={() => setConfirm(true)}>
              <Icon>delete</Icon>&nbsp;{t(`element.delete`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderNavigate = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container>
          <Grid item xs={6}>
            <NavLink to={`/${mod}`}>
              <MDButton variant='contained' color='dark'>
                <Icon>arrow_back</Icon>&nbsp;{t(`element.back`)}
              </MDButton>
            </NavLink>
          </Grid>
          {navigate.first === '' ? null : (
            <Grid item xs={6}>
              <MDBox display='flex' justifyContent='flex-end' gap={1}>
                <NavLink to={`/${mod}/${navigate.first}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.first === id}
                  >
                    <Icon>first_page</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.prev}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.prev === id}
                  >
                    <Icon>navigate_before</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.next}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.next === id}
                  >
                    <Icon>navigate_next</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.last}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.last === id}
                  >
                    <Icon>last_page</Icon>
                  </MDButton>
                </NavLink>
              </MDBox>
            </Grid>
          )}
        </Grid>
      </Card>
    );
  };

  const renderButton = () => {
    return (
      <MDBox display='flex' justifyContent='end' alignItems='center' gap={1} flexWrap='wrap'>
        {printBlock ? (
          <MDTypography variant='caption' color={campaignBlocked() ? 'error' : 'warning'}>
            {printBlock}
          </MDTypography>
        ) : null}
        <MDButton
          variant='gradient'
          color='warning'
          onClick={copy}
          disabled={Boolean(unsaved)}
          title={unsaved ? 'กดบันทึกก่อนคัดลอก' : undefined}
        >
          <Icon>content_copy</Icon>&nbsp;{t(`element.copy`)}
        </MDButton>
        <MDButton
          variant='gradient'
          color='info'
          onClick={print}
          disabled={Boolean(printBlock)}
          title={
            promoSaved
              ? 'ป้ายโปรคิดราคาและโปรโมชันที่ใช้อยู่ ณ ตอนพิมพ์ (ไม่ใช้ราคาในรายการด้านล่าง) · ตั้งเครื่องพิมพ์ขนาดจริง 100%'
              : 'ตั้งเครื่องพิมพ์ขนาดจริง 100%'
          }
        >
          <Icon>print</Icon>&nbsp;{t(`element.print`)}{' '}
          {attr.print_no > 0 ? `(${attr.print_no})` : ''}
        </MDButton>
        <MDButton variant='gradient' color='info' onClick={sticker} disabled={Boolean(printBlock)}>
          <Icon>print</Icon>&nbsp;Sticker {attr.sticker_no > 0 ? `(${attr.sticker_no})` : ''}
        </MDButton>
        {/* ป้ายโปรแบบใหม่ (layout PROMO · 29 ต.ค. 2569) ตัวส่งออก PNG ยังวาดแบบเดิม — ซ่อนปุ่ม ใช้ปุ่มพิมพ์ (PDF) */}
        {promoSaved ? null : (
          <TagExportPng
            tagId={attr.id}
            code={attr.code}
            app={app}
            onNotify={showNotify}
            confirmExpired={() => confirmPrice().then((go) => (go ? confirmExpired() : null))} // ส่งออกราคาที่เก็บไว้ ตรวจกับราคาวันนี้ก่อนแบบปุ่มพิมพ์
            disabled={Boolean(printBlock)}
            deniedText={attr.campaign ? campaignText(attr.campaign) : ''}
          />
        )}
      </MDBox>
    );
  };

  /* แถบ "ก่อนพิมพ์" ของป้ายโปร: พร้อมพิมพ์ / รูป AI รออนุมัติ / ไม่ได้พิมพ์ — พาไปแก้ตรงจุด แทนหน้าต่างถามซ้อนตอนกดพิมพ์ (2 พ.ย. 2569) */
  const renderReadiness = () => {
    if (!promoSaved || !artSum || !('cards' in artSum)) return null;
    const tile = (color, bg, border, count, text, action) => (
      <Grid item xs={12} md={4}>
        <MDBox
          display='flex'
          alignItems='center'
          gap={1.5}
          px={1.75}
          py={1.25}
          sx={{ bgcolor: bg, border: `1px solid ${border}`, borderRadius: 2, height: '100%' }}
        >
          <MDBox
            sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flex: 'none' }}
          />
          <MDBox sx={{ flex: 1, minWidth: 0 }}>
            <MDTypography variant='h5' sx={{ color, lineHeight: 1.1 }}>
              {count.toLocaleString('th-TH')} ใบ
            </MDTypography>
            <MDTypography variant='caption' color='text' display='block'>
              {text}
            </MDTypography>
          </MDBox>
          {action}
        </MDBox>
      </Grid>
    );
    return (
      <Grid container spacing={1.5} mt={0.5}>
        {tile('#2e7d32', '#edf7ee', '#cfe9d1', artSum.ready, 'พร้อมพิมพ์', null)}
        {tile(
          '#b35c00',
          '#fff4e5',
          '#ffd9a8',
          artSum.waitingCards,
          artSum.waitingCards
            ? 'รูป AI รออนุมัติ · ถ้าพิมพ์ตอนนี้จะใช้รูปเดิม'
            : 'รูป AI รออนุมัติ',
          artSum.canReview ? (
            <MDButton
              size='small'
              color='warning'
              onClick={() => setJump({ kind: 'review', n: Date.now() })}
            >
              ตรวจรูป {artSum.canReview.toLocaleString('th-TH')} ใบ
            </MDButton>
          ) : null
        )}
        {tile(
          '#b3261e',
          '#fdecea',
          '#f8c9c4',
          artSum.unprinted,
          artSum.unprinted ? 'ไม่ได้พิมพ์ · ไม่มีราคาที่สาขานี้' : 'ไม่ได้พิมพ์',
          artSum.unprinted ? (
            <MDButton
              size='small'
              variant='outlined'
              color='error'
              onClick={() => setJump({ kind: 'unprinted', n: Date.now() })}
            >
              ดูใบนั้น
            </MDButton>
          ) : null
        )}
      </Grid>
    );
  };

  /* ช่องตั้งค่าระดับชุด (ขนาด · สาขา · หมายเหตุ + บันทึก) ส่งเข้าการ์ด "ตั้งค่าทั้งชุด" ของหน้าชุดป้ายโปร */
  const remarkDirty = Boolean(saved) && String(attr.remark ?? '') !== String(saved.remark ?? '');
  const renderSettings = () => (
    <MDBox>
      {renderInfo(true)}
      <MDBox display='flex' alignItems='center' justifyContent='flex-end' gap={1.5} mt={1.5}>
        <MDTypography
          variant='caption'
          color={unsaved || remarkDirty ? 'warning' : 'success'}
          role='status'
        >
          {unsaved
            ? 'เปลี่ยนขนาด/สาขาแล้ว · กดบันทึกก่อนพิมพ์'
            : remarkDirty
            ? 'แก้หมายเหตุแล้ว · ยังไม่บันทึก'
            : 'บันทึกแล้ว'}
        </MDTypography>
        <MDButton color='success' size='small' onClick={edit}>
          <Icon>save</Icon>&nbsp;{t(`element.save`)}
        </MDButton>
      </MDBox>
    </MDBox>
  );

  /* หน้าชุดป้าย = หน้าเดียว ไม่มีแท็บ (2 พ.ย. 2569 · แบบ Claude Design "หน้าชุดป้ายรวมแท็บ"):
     หัวหน้า (รหัส · ชิปขนาด/จำนวนใบ · ปุ่มพิมพ์) → แถบ "ก่อนพิมพ์" → TagList (ป้ายโปร = TagArtPanel ทั้งหน้า: ตั้งค่าทั้งชุด + การ์ดตามแผ่น + ภาพ AI)
     ชุดแบบเดิม = การ์ดตั้งค่า + รายการแบบเดิม + กล่องชวนเปลี่ยนเป็นป้ายโปร 7×5 */
  const renderOpen = () => {
    const chips = [];
    if (saved) {
      chips.push(
        promoSaved
          ? 'ป้ายโปร 7×5 ซม.'
          : savedTpl
          ? savedTpl.name || savedTpl.code
          : 'แบบเดิม · ตามค่าตั้งต้น'
      );
    }
    if (promoSaved && artSum && 'cards' in artSum)
      chips.push(
        `${artSum.cards.toLocaleString('th-TH')} ใบ · ${artSum.sheets.toLocaleString(
          'th-TH'
        )} แผ่น (${artSum.perSheet} ใบ/แผ่น)`
      );
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderNavigate()}
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} xl={6}>
                  <MDBox display='flex' alignItems='center' flexWrap='wrap' gap={1}>
                    <MDTypography variant='h3'>#{attr.code}</MDTypography>
                    {chips.map((c) => (
                      <Chip key={c} size='small' label={c} />
                    ))}
                    {attr.campaign ? (
                      <Chip
                        size='small'
                        variant='outlined'
                        color={attr.campaign.status === 'ACTIVE' ? 'success' : 'error'}
                        label={`จากแคมเปญ ${attr.campaign.code} · ${attr.campaign.status_label}`}
                      />
                    ) : null}
                  </MDBox>
                  {(saved && saved.remark) || attr.created_at ? (
                    <MDTypography variant='caption' color='text' display='block' mt={0.5}>
                      {[
                        saved && saved.remark,
                        attr.created_at ? `สร้าง ${Func.dateTimeDisplay(attr.created_at)}` : '',
                        attr.print_no > 0 ? `พิมพ์แล้ว ${attr.print_no} ครั้ง` : '',
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </MDTypography>
                  ) : null}
                </Grid>
                <Grid item xs={12} xl={6}>
                  {renderButton()}
                </Grid>
                {promoSaved ? (
                  <Grid item xs={12}>
                    {renderReadiness()}
                  </Grid>
                ) : null}
                {!promoSaved && promoTpl && attr.code ? (
                  <Grid item xs={12}>
                    <MDBox
                      display='flex'
                      alignItems='center'
                      flexWrap='wrap'
                      gap={1.5}
                      px={2}
                      py={1.5}
                      sx={{ bgcolor: '#e8f1fd', borderRadius: 2 }}
                    >
                      <MDTypography variant='button' color='text' sx={{ flex: '1 1 320px' }}>
                        ชุดนี้ยังเป็นป้ายแบบเดิม · คำโปร ราคาบนป้าย และรูปสินค้า AI ใช้กับป้ายโปร
                        7×5 ซม. แบบใหม่เท่านั้น (ราคาคิดจากราคาสาขาและแคมเปญให้เอง)
                      </MDTypography>
                      <MDButton
                        color='info'
                        size='small'
                        disabled={unsaved}
                        title={unsaved ? 'กดบันทึกก่อน' : ''}
                        onClick={toPromo}
                      >
                        <Icon>auto_awesome</Icon>&nbsp;เปลี่ยนเป็นป้ายโปร 7×5 ซม.
                      </MDButton>
                    </MDBox>
                  </Grid>
                ) : null}
                {promoSaved ? null : (
                  <Grid item xs={12} sm={12} md={12} lg={4}>
                    <MDBox className='card' p={2}>
                      {renderInfo()}
                      <Grid container sx={{ mt: 2 }}>
                        <Grid item xs={12} display='flex' justifyContent='flex-end'>
                          <MDButton color='success' onClick={edit}>
                            <Icon>save</Icon>&nbsp;{t(`element.save`)}
                          </MDButton>
                        </Grid>
                      </Grid>
                    </MDBox>
                  </Grid>
                )}
                <Grid item xs={12} lg={12}>
                  <MDBox className={promoSaved ? '' : 'card'}>
                    <TagList
                      parent={attr}
                      promo={promoSaved}
                      settings={promoSaved ? renderSettings() : null}
                      onSummary={promoSaved ? setArtSum : null}
                      jump={jump}
                      reloadKey={
                        saved ? `${saved.branch_id}|${saved.template_id}|${priceTick}` : ''
                      }
                    />
                  </MDBox>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderPrint = () => {
    return (
      <Dialog
        open={modalPrint}
        maxWidth='xl'
        keepMounted
        aria-describedby='dialog-print'
        fullScreen
        onClose={() => setModalPrint(false)}
      >
        <DialogTitle sx={{ pr: 6 }}>
          #{attr.code}
          {/* เครื่องพิมพ์ที่ตั้ง "พอดีหน้ากระดาษ" ย่อป้ายลง 3–5% ป้ายจะไม่ใช่ 7×5 ซม. (ประเมินป้าย 29 ต.ค. 2569) */}
          <MDTypography
            component='span'
            variant='button'
            color='warning'
            fontWeight='medium'
            role='note'
            sx={{ ml: 2 }}
          >
            ตอนสั่งพิมพ์ ตั้งขนาดเป็น &quot;ขนาดจริง / 100%&quot; ห้ามเลือก &quot;พอดีหน้ากระดาษ
            (Fit to page)&quot;
            {promoSaved ? ' · วัดป้ายใบแรกต้องกว้าง 7 ซม.' : ''}
          </MDTypography>
          <IconButton
            aria-label='close'
            onClick={() => setModalPrint(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          {attr.print === '' ? null : (
            <>
              {/* เห็นผ่านกรอบระหว่างที่ PDF ยังไม่ขึ้น ไม่ให้จอขาวเฉย ๆ */}
              <MDTypography
                variant='button'
                color='text'
                sx={{ position: 'absolute', top: '45%', left: 0, right: 0, textAlign: 'center' }}
              >
                กำลังเตรียมไฟล์ PDF…
              </MDTypography>
              <Iframe
                key={attr.print} // เปลี่ยนไฟล์ = ล้างของเดิม ไม่โชว์ PDF ชุดก่อนระหว่างโหลด (กันสั่งพิมพ์ผิดชุด)
                url={attr.print}
                width='100%'
                height='100%'
                id='print'
                className=''
                display='initial'
                position='relative'
                frameBorder='0'
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    );
  };

  const renderPage = () => {
    if (id === undefined) return renderList();
    return renderOpen();
  };

  useEffect(() => {
    alive.current = true;
    i18n.changeLanguage(app.lang);

    init();
    return () => {
      alive.current = false;
    };
  }, [id]);
  // ล้างตัวจับเวลาแจ้งเตือนเฉพาะตอนออกจากหน้า — ถ้าล้างตอนเปลี่ยน id ข้อความ "สร้างป้าย N รายการ" จะค้างไม่ปิดเอง (18 ต.ค. 2569)
  useEffect(() => () => clearTimeout(notifyTimer.current), []);
  // ผลนับ A/B/C เปลี่ยน (เปลี่ยนสาขา/หมวด/เกรด) = ต้องกดบันทึกใหม่ ไม่ให้ยืนยันด้วยตัวเลขเก่า (29 ต.ค. 2569)
  useEffect(() => setConfirm(false), [rankStatus.active, rankStatus.reason, rankStatus.total]);

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.product`), t(`menu.tag`)]} />
      {renderPage()}
      {renderAdd()}
      {renderPrint()}
      {renderDelete()}
      <Footer />
      {renderLoading()}
      {notify}
    </DashboardLayout>
  );
}

export default Tag;
